import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Play, ArrowRight, Phone } from 'lucide-react';
import {
    HERO_CONTENT,
    PILLARS,
    WHAT_WE_DO,
    ABOUT_TEXT,
    INDUSTRIES,
    TESTIMONIAL,
    ARTICLES,
    IMAGERY,
    CONTACT_INFO,
} from '@/lib/content';
import { HERO, HOME } from '@/constants/testIds';
import ClientMarquee from '@/components/sections/ClientMarquee';
import SectionHeading from '@/components/sections/SectionHeading';
import SEO from '@/components/SEO';

export default function Home() {
    return (
        <div>
            <SEO path="/" />
            {/* ---------- HERO (clean white, client studio image) ---------- */}
            <section
                data-testid={HERO.section}
                className="relative pt-24 bg-white overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-12 py-8 md:py-12 lg:py-14 items-center">
                    <div className="lg:col-span-6 relative z-10">
                        <div className="text-[10px] md:text-sm uppercase tracking-[0.3em] md:tracking-[0.35em] text-brand-orange font-bold mb-4 md:mb-5 flex items-center gap-3">
                            <span className="inline-block h-px w-8 md:w-10 bg-brand-orange" />
                            {HERO_CONTENT.eyebrow}
                        </div>
                        <h1
                            data-testid={HERO.title}
                            aria-label="Direct Response Advertising Strategies"
                            className="font-display font-black text-brand-navy text-[40px] sm:text-6xl md:text-[64px] lg:text-[72px] xl:text-[84px] leading-[0.92] tracking-[-0.02em]"
                        >
                            Direct Response<br />
                            Advertising<br />
                            <span className="italic font-medium text-brand-orange">Strategies.</span>
                        </h1>
                        <p
                            data-testid={HERO.subtitle}
                            className="text-brand-navy/75 text-base md:text-lg max-w-xl mt-5 md:mt-6 leading-relaxed"
                        >
                            {HERO_CONTENT.subtitle}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-6">
                            <Link
                                to="/contact"
                                data-testid={HERO.primaryCta}
                                className="inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-navy text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                            >
                                {HERO_CONTENT.primaryCta} <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/work"
                                data-testid={HERO.secondaryCta}
                                className="inline-flex items-center gap-3 border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                            >
                                <Play className="w-4 h-4" /> {HERO_CONTENT.secondaryCta}
                            </Link>
                        </div>

                        <div className="grid grid-cols-3 gap-6 mt-8 pt-6 border-t border-brand-navy/10 max-w-lg">
                            {ABOUT_TEXT.stats.slice(0, 3).map((s) => (
                                <div key={s.label}>
                                    <div className="font-display font-extrabold text-3xl md:text-4xl text-brand-navy leading-none">
                                        {s.value}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-navy/55 mt-2">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-6 relative">
                        <div className="relative aspect-[5/4] overflow-hidden bg-brand-navy">
                            <img
                                src={IMAGERY.studio}
                                alt="CFL Ad Group production studio shoot"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-5 -left-5 bg-brand-navy text-white px-5 py-4 max-w-[260px]">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-orange font-bold mb-1">
                                In-house production
                            </div>
                            <div className="font-display font-extrabold text-base lg:text-lg leading-snug">
                                From script to on-air in under two weeks.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- CLIENT MARQUEE (orange) ---------- */}
            <ClientMarquee />

            {/* ---------- PILLARS ---------- */}
            <section
                data-testid={HOME.pillars}
                className="bg-white"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-10 lg:mb-12">
                        <div className="lg:col-span-5">
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-4">
                                What we lead with
                            </div>
                            <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-[44px] tracking-tight text-brand-navy leading-[1.02]">
                                Three pillars. <span className="italic font-medium text-brand-orange">One playbook</span> built for measurable response.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 flex items-end">
                            <p className="text-brand-navy/70 text-lg leading-relaxed">
                                We combine brand fundamentals, full-funnel media buying, and high-frequency
                                creative — engineered to move metal off the lot, not just rack up impressions.
                            </p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 border-t-2 border-brand-navy">
                        {PILLARS.map((p, idx) => (
                            <article
                                key={p.key}
                                data-testid={
                                    p.key === 'brand' ? HOME.pillarBuildBrand :
                                    p.key === 'media' ? HOME.pillarMedia :
                                    HOME.pillarSocial
                                }
                                className={`group relative p-7 md:p-8 lg:p-9 ${
                                    idx !== 2 ? 'md:border-r border-brand-navy/10' : ''
                                } border-b md:border-b-0 border-brand-navy/10 transition-colors`}
                            >
                                <div className="flex items-baseline justify-between mb-6">
                                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange font-bold">
                                        {p.number} / 03
                                    </span>
                                    <ArrowUpRight className="w-5 h-5 text-brand-navy/30 group-hover:text-brand-orange group-hover:rotate-12 transition-all" />
                                </div>
                                <h3 className="font-display font-extrabold text-2xl md:text-3xl text-brand-navy leading-tight tracking-tight mb-4">
                                    {p.title}
                                </h3>
                                <p className="text-brand-navy/65 text-[15px] leading-relaxed">
                                    {p.body}
                                </p>
                                <Link
                                    to="/services"
                                    className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.25em] text-brand-orange font-bold hover:gap-3 transition-all"
                                >
                                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- WHAT WE DO ---------- */}
            <section
                data-testid={HOME.whatWeDo}
                className="bg-brand-cream"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20">
                    <SectionHeading
                        eyebrow="What we do for you"
                        title="Six capabilities. Engineered to perform."
                        description="The fundamentals every brand needs — and the specialised firepower automotive groups depend on."
                    />
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 mt-10 lg:mt-12 border-t border-l border-brand-navy/15">
                        {WHAT_WE_DO.map((item, idx) => (
                            <div
                                key={item.title}
                                className="border-b border-r border-brand-navy/15 p-7 md:p-8 lg:p-9 bg-white group hover:bg-brand-navy transition-colors duration-300"
                            >
                                <div className="text-xs font-mono uppercase tracking-[0.3em] text-brand-orange mb-5 font-bold">
                                    {String(idx + 1).padStart(2, '0')}
                                </div>
                                <h3 className="font-display font-extrabold text-2xl md:text-3xl text-brand-navy group-hover:text-white leading-tight mb-4 transition-colors">
                                    {item.title}
                                </h3>
                                <p className="text-brand-navy/65 group-hover:text-white/85 text-[15px] leading-relaxed transition-colors">
                                    {item.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- ABOUT SNAPSHOT (navy band + dealership image) ---------- */}
            <section
                data-testid={HOME.aboutSnapshot}
                className="relative bg-brand-navy text-white"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                                src={IMAGERY.dealership}
                                alt="CFL Ad Group on-location production"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 -right-6 bg-brand-orange text-white px-6 py-5">
                            <div className="font-display font-extrabold text-4xl leading-none">30+</div>
                            <div className="text-[10px] uppercase tracking-[0.3em] mt-1">years on air</div>
                        </div>
                    </div>
                    <div className="lg:col-span-7 lg:pl-6">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-4">
                            {ABOUT_TEXT.eyebrow}
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-[48px] xl:text-[56px] text-white tracking-tight leading-[1.02] mb-6">
                            {ABOUT_TEXT.headline}
                        </h2>
                        <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                            {ABOUT_TEXT.body}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 pt-8 border-t border-white/15">
                            {ABOUT_TEXT.stats.map((s) => (
                                <div key={s.label}>
                                    <div className="font-display font-extrabold text-3xl md:text-4xl text-brand-orange leading-none">
                                        {s.value}
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] text-white/55 mt-2">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Link
                            to="/about"
                            className="inline-flex items-center gap-3 bg-white text-brand-navy hover:bg-brand-orange hover:text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                        >
                            Learn More <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ---------- INDUSTRIES ---------- */}
            <section data-testid={HOME.industries} className="bg-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-12">
                        <SectionHeading
                            eyebrow="Our Industries"
                            title="Where our work lives."
                            description="From the showroom floor to the next emerging platform — we tailor every campaign to the audience that buys."
                        />
                        <Link
                            to="/industries"
                            className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-orange text-xs uppercase tracking-[0.25em] font-bold self-start"
                        >
                            All industries <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {INDUSTRIES.map((ind, idx) => (
                            <article
                                key={ind.key}
                                className="relative overflow-hidden bg-brand-navy text-white group min-h-[300px]"
                            >
                                <img
                                    src={ind.image}
                                    alt={ind.title}
                                    className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/85 to-brand-navy/30" />
                                <div className="relative p-7 md:p-8 lg:p-9 h-full flex flex-col justify-end">
                                    <div className="flex items-start justify-between gap-6 mb-4">
                                        <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange font-bold">
                                            Industry · {String(idx + 1).padStart(2, '0')}
                                        </span>
                                        <ArrowUpRight className="w-5 h-5 text-white/60 group-hover:text-brand-orange transition-colors" />
                                    </div>
                                    <h3 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight mb-3">
                                        {ind.title}
                                    </h3>
                                    <p className="text-white/80 leading-relaxed text-sm md:text-base">{ind.body}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- WORK / VIDEO REEL ---------- */}
            <section data-testid={HOME.workReel} className="bg-brand-cream">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-4">
                                The Reel
                            </div>
                            <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-[44px] text-brand-navy tracking-tight leading-tight">
                                Direct response.<br />
                                <span className="italic font-medium text-brand-orange">Direct results.</span>
                            </h2>
                        </div>
                        <Link
                            to="/work"
                            className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-orange text-xs uppercase tracking-[0.25em] font-bold self-start"
                        >
                            View full reel <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="relative aspect-video bg-brand-navy overflow-hidden shadow-[0_30px_80px_-30px_rgba(31,27,77,0.5)]">
                        <iframe
                            title="CFL Ad Group work reel"
                            src="https://www.youtube.com/embed/uE_FJ8pQOrw?rel=0&modestbranding=1"
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            </section>

            {/* ---------- TESTIMONIAL (orange band) ---------- */}
            <section
                data-testid={HOME.testimonial}
                className="relative bg-brand-orange text-white"
            >
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-16 md:py-20 lg:py-24 text-center">
                    <div className="font-display text-7xl md:text-8xl text-white/30 leading-none mb-4">“</div>
                    <p className="font-display font-extrabold text-white text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight italic">
                        {TESTIMONIAL.quote}
                    </p>
                    <div className="mt-10 inline-flex items-center gap-3 text-white/90">
                        <span className="h-px w-12 bg-white/50" />
                        <span className="text-xs uppercase tracking-[0.3em] font-bold">
                            {TESTIMONIAL.author} · {TESTIMONIAL.org}
                        </span>
                    </div>
                </div>
            </section>

            {/* ---------- ARTICLES ---------- */}
            <section data-testid={HOME.articles} className="bg-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-18 lg:py-20">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-12">
                        <SectionHeading eyebrow="Articles" title="From the studio." />
                        <Link
                            to="/articles"
                            className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-orange text-xs uppercase tracking-[0.25em] font-bold"
                        >
                            All articles <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {ARTICLES.map((a) => (
                            <Link
                                to="/articles"
                                key={a.slug}
                                className="group block border border-brand-navy/10 hover:border-brand-orange hover:shadow-[0_20px_50px_-20px_rgba(31,27,77,0.2)] transition-all bg-white"
                            >
                                <div className="aspect-[4/3] overflow-hidden bg-brand-navy">
                                    <img
                                        src={a.image}
                                        alt={a.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-7">
                                    <div className="text-[11px] uppercase tracking-[0.25em] text-brand-orange mb-3 font-bold">
                                        {a.date} · {a.readTime}
                                    </div>
                                    <h3 className="font-display font-extrabold text-2xl text-brand-navy mb-3 leading-tight group-hover:text-brand-orange transition-colors">
                                        {a.title}
                                    </h3>
                                    <p className="text-brand-navy/65 text-sm leading-relaxed line-clamp-3">
                                        {a.excerpt}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- CONTACT CTA (navy) ---------- */}
            <section data-testid={HOME.cta} className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-orange mb-4">
                            Ready when you are
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-[48px] xl:text-[56px] text-white tracking-tight leading-[1.02]">
                            Let’s put your dealership <span className="text-brand-orange italic font-medium">on air this quarter.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex flex-col gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-between gap-3 bg-brand-orange hover:bg-white hover:text-brand-navy text-white px-7 py-5 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                        >
                            Schedule a Call <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <a
                            href={CONTACT_INFO.phoneHref}
                            className="inline-flex items-center justify-between gap-3 border-2 border-white/30 hover:border-brand-orange text-white px-7 py-5 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                        >
                            <span className="flex items-center gap-3"><Phone className="w-4 h-4" /> {CONTACT_INFO.phone}</span>
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
